import { SignupContext } from '@jetshop/core/components/Auth/signup-context'
import useAuth from '@jetshop/core/components/AuthContext/useAuth'
import { trackRegisterEvent } from '@jetshop/core/analytics/tracking'
import { useTracker } from '@jetshop/core/analytics/Analytics'
import { useApolloClient } from '@apollo/react-hooks'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useIntl } from '@jetshop/intl'
import { css } from 'linaria'
import React, { useContext } from 'react'
import VbButton from '../../Core/SiteButton'
import VbCheckbox from '../../Core/Form/VbCheckbox'
import { theme } from '../../Theme'
import { GlobalError } from '../../Forms/GlobalError'
import Link from '../../Core/Link'
import { useRegisterCustomer } from './RegisterCustomer/useRegisterCustomer'
import PrivateCustomerFields from './RegisterCustomer/PrivateCustomerFields'
import CompanyCustomerFields from './RegisterCustomer/CompanyCustomerFields'
import { CustomerType } from './CustomerType'
import GamifieraAvatars from '../../Gamifiera/GamifieraAvatars'

export const DEFAULT_SIGNUP_HERO_IMAGE =
   'https://verktygsboden.se/pub_docs/files/Restoration-Show_image002.jpg'

/** Warm brown from Restoration Show creative; panel = this color at low opacity over white. */
export const SIGNUP_PANEL_TINT = '#daceb4'

const splitRoot = css`
   display: flex;
   min-height: 100vh;
   width: 100%;
   ${theme.below.md} {
      flex-direction: column;
   }
`

const splitHero = css`
   flex: 0 0 50%;
   width: 50%;
   max-width: 50%;
   position: relative;
   min-height: 420px;
   background: #ddd;
   ${theme.below.md} {
      flex: none;
      width: 100%;
      max-width: none;
      min-height: min(45vh, 320px);
      max-height: 360px;
   }
`

const heroImg = css`
   position: absolute;
   inset: 0;
   width: 100%;
   height: 100%;
   object-fit: cover;
   object-position: center top;
   display: block;
`

const splitPanel = css`
   flex: 1 1 50%;
   width: 50%;
   max-width: 50%;
   background: rgba(139, 74, 17, 0.16);
   @supports (background: color-mix(in srgb, white, black)) {
      background: color-mix(in srgb, ${SIGNUP_PANEL_TINT} 3%, white 88%);
   }
   padding: clamp(2rem, 5vw, 4rem) clamp(1.5rem, 4vw, 3.5rem);
   display: flex;
   flex-direction: column;
   justify-content: flex-start;
   ${theme.below.md} {
      width: 100%;
      max-width: none;
      flex: 1;
   }
`

const panelInner = css`
   width: 100%;
   max-width: 26rem;
   margin: 0 auto;
`

const panelTitle = css`
   font-family: var(--font-industry);
   font-weight: 700;
   font-size: clamp(1.75rem, 3vw, 2.25rem);
   color: #000;
   margin: 0 0 0.25rem;
   line-height: 1.2;
`

const panelIntro = css`
   font-family: var(--font-grotesk);
   font-size: 0.9375rem;
   line-height: 1.5;
   color: #000;
   margin: 0 0 1.5rem;
   opacity: 0.9;
`

const fieldWrap = css`
   margin-bottom: 1.25rem;
   & input {
      border: 1px solid #000 !important;
      border-radius: 0 !important;
      background: #fff !important;
      min-height: 2.75rem;
   }
`

const labelRow = css`
   display: flex;
   justify-content: space-between;
   align-items: baseline;
   gap: 1rem;
   margin-bottom: 0.35rem;
   font-family: var(--font-industry);
`

const labelMain = css`
   font-weight: 600;
   font-size: 1rem;
   color: #000;
`

const labelRequired = css`
   font-size: 0.8125rem;
   font-style: italic;
   color: #000;
   opacity: 0.8;
   flex-shrink: 0;
`

const customerTypeWrap = css`
   margin-bottom: 1.5rem;
`

const checkboxRow = css`
   display: flex;
   flex-wrap: wrap;
   gap: 1rem 1.5rem;
   margin-bottom: 1rem;
`

const signupCheckboxLabel = css`
   font-family: var(--font-industry);
   font-size: 0.9375rem;
   font-weight: 600;
   color: #000;
   line-height: 1.35;

   ${theme.below.md} {
      font-size: ${theme.font.size.m};
   }
`

const signupVbCheckbox = css`
   gap: 0.5rem;
`

const termsVbCheckbox = css`
   align-items: flex-start;
   gap: 0.5rem;

   & input[type='checkbox'] {
      margin-top: 0.15rem;
      flex-shrink: 0;
   }
`

const termsRow = css`
   margin-bottom: 1rem;
`

const benefitsList = css`
   list-style: none;
   padding: 0;
   margin: 0 0 1.5rem;
   font-family: var(--font-grotesk);

   & li {
      font-size: 0.9375rem;
      line-height: 1.6;
      color: #000;
      opacity: 0.9;

      &::before {
         content: '•';
         margin-right: 0.5rem;
         font-size: 1.125rem;
      }
   }
`

const legalNote = css`
   font-family: var(--font-grotesk);
   font-size: 0.75rem;
   line-height: 1.45;
   color: #000;
   opacity: 0.75;
   margin: 0 0 1.25rem;

   & a {
      color: inherit;
      text-decoration: underline;
   }
`

const termsError = css`
   color: #a61200;
   font-size: 0.8125rem;
   margin: -0.5rem 0 0.75rem;
   font-family: var(--font-industry);
`

const signUpSubmitButton = css`
   && {
      width: 100%;
      border-radius: 2rem;
   }
   margin-top: 0.5rem;
   box-sizing: border-box;
   padding: 1rem 1.25rem;
   font-family: var(--font-industry);
   font-size: 0.875rem;
   font-weight: 700;
   letter-spacing: 0.06em;
   line-height: 1.25;

   ${theme.below.md} {
      padding: 0.875rem 1rem;
      font-size: 0.8125rem;
   }
`

export function FormFieldRow({ fieldId, labelText, requiredHint, children }) {
   return (
      <div className={fieldWrap}>
         <div className={labelRow}>
            <label htmlFor={fieldId} className={labelMain}>
               {labelText}
            </label>
            <span className={labelRequired}>{requiredHint}</span>
         </div>
         {children}
      </div>
   )
}

/** Optional leading `+`, then ASCII digits only (no spaces). Typical E.164 subscriber length. */
function isValidPhoneFormat(raw) {
   const s = String(raw).trim()
   if (!s) return false
   if (!/^\+?\d+$/.test(s)) return false
   const digitCount = s.startsWith('+') ? s.length - 1 : s.length
   return digitCount >= 8 && digitCount <= 15
}

const initialValues = {
   firstName: '',
   lastName: '',
   mobilePhone: '',
   emailAddress: '',
   password: '',
   organizationNumber: '',
   companyName: '',
   streetName: '',
   postalCode: '',
   city: '',
   acceptsEmail: true,
   acceptsSms: true,
   acceptTerms: false,
}

function validate(values, t, isCompany) {
   const errors = {}

   if (!values.firstName?.trim()) {
      errors.firstName = t('This field is required')
   }
   if (!values.lastName?.trim()) {
      errors.lastName = t('This field is required')
   }
   if (!values.mobilePhone?.trim()) {
      errors.mobilePhone = t('This field is required')
   } else if (!isValidPhoneFormat(values.mobilePhone)) {
      errors.mobilePhone = t('Your phone number is invalid')
   }
   if (!values.emailAddress?.trim()) {
      errors.emailAddress = t('This field is required')
   } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.emailAddress.trim())) {
      errors.emailAddress = t('Invalid email address')
   }
   if (!values.password) {
      errors.password = t('This field is required')
   }
   if (isCompany) {
      if (!values.organizationNumber?.trim()) {
         errors.organizationNumber = t('This field is required')
      }
      if (!values.companyName?.trim()) {
         errors.companyName = t('This field is required')
      }
      if (!values.streetName?.trim()) {
         errors.streetName = t('This field is required')
      }
      if (!values.postalCode?.trim()) {
         errors.postalCode = t('This field is required')
      }
      if (!values.city?.trim()) {
         errors.city = t('This field is required')
      }
   }
   if (!values.acceptTerms) {
      errors.acceptTerms = t('You must accept the terms to register')
   }
   return errors
}

/**
 * Split hero + signup panel (image left, form on a light tint of `SIGNUP_PANEL_TINT` / `#8b4a11`).
 */
export default function SignUpForm({
   heroImageSrc = DEFAULT_SIGNUP_HERO_IMAGE,
   heroImageAlt = '',
   panelPromoImageSrc,
   panelPromoImageAlt = '',
}) {
   const { isUserTypeBusiness } = useContext(SignupContext)
   const t = useIntl()
   const { logIn } = useAuth()
   const client = useApolloClient()
   const track = useTracker()
   const [registerCustomer] = useRegisterCustomer()

   const requiredHint = t('(Required)')
   const imageAlt = heroImageAlt || t('Sign up')

   return (
      <div className={splitRoot}>
         <aside className={splitHero}>
            <img
               className={heroImg}
               src={heroImageSrc}
               alt={imageAlt}
               loading="eager"
               decoding="async"
            />
         </aside>
         <div className={splitPanel}>
            <div className={panelInner}>
               <h1 className={panelTitle}>{t('Sign up')}</h1>
               <GamifieraAvatars src={panelPromoImageSrc} alt={panelPromoImageAlt} />
               {/* <p className={panelIntro}>{t('sign up welcom message')}</p> */}
               <p className={panelIntro}>{t('As a Verktygsboden member you get access to:')}</p>
               <ul className={benefitsList}>
                  <li>{t('Order history')}</li>
                  <li>{t('Change your information and interests')}</li>
                  <li>{t('Saved favorites')}</li>
                  <li>{t('Community')}</li>
                  <li>{t('Competitions and offers')}</li>
               </ul>

               <div className={customerTypeWrap}>
                  <CustomerType />
               </div>

               <Formik
                  initialValues={initialValues}
                  validateOnChange
                  validate={values => validate(values, t, isUserTypeBusiness)}
                  onSubmit={async (values, actions) => {
                     actions.setSubmitting(true)
                     actions.setStatus(undefined)
                     try {
                        const { data, errors } = await registerCustomer({
                           firstName: values.firstName.trim(),
                           lastName: values.lastName.trim(),
                           emailAddress: values.emailAddress.trim(),
                           password: values.password,
                           mobilePhone: values.mobilePhone.trim(),
                           customerType: isUserTypeBusiness
                              ? 'COMPANY'
                              : 'PRIVATE',
                           acceptsEmail: values.acceptsEmail,
                           acceptsSms: values.acceptsSms,
                           acceptsPostal: false,
                           organizationNumber: values.organizationNumber?.trim(),
                           companyName: values.companyName?.trim(),
                           streetName: values.streetName?.trim(),
                           postalCode: values.postalCode?.trim(),
                           city: values.city?.trim(),
                        })
                        if (errors?.length) {
                           actions.setStatus({
                              apiError:
                                 errors[0]?.message ||
                                 t('Something went wrong. Please try again.'),
                           })
                           return
                        }
                        const token = data?.signUp?.token?.value
                        if (!token) {
                           actions.setStatus({
                              apiError: t(
                                 'Something went wrong. Please try again.'
                              ),
                           })
                           return
                        }
                        track(trackRegisterEvent())
                        await client.resetStore()
                        logIn(token)
                     } catch (e) {
                        const message =
                           e?.graphQLErrors?.[0]?.message ||
                           e?.message ||
                           t('Something went wrong. Please try again.')
                        actions.setStatus({ apiError: message })
                     } finally {
                        actions.setSubmitting(false)
                     }
                  }}
               >
                  {({ status, isSubmitting }) => (
                     <Form>
                        {isUserTypeBusiness ? (
                           <CompanyCustomerFields requiredHint={requiredHint} />
                        ) : (
                           <PrivateCustomerFields requiredHint={requiredHint} />
                        )}

                        <div className={checkboxRow}>
                           <Field name="acceptsEmail">
                              {({ field }) => (
                                 <VbCheckbox
                                    id="simple-signup-accepts-email"
                                    name={field.name}
                                    className={signupVbCheckbox}
                                    showDefaultCheckmarkIcon={false}
                                    checked={!!field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    label={
                                       <span className={signupCheckboxLabel}>
                                          {t('Accept email')}
                                       </span>
                                    }
                                 />
                              )}
                           </Field>
                           <Field name="acceptsSms">
                              {({ field }) => (
                                 <VbCheckbox
                                    id="simple-signup-accepts-sms"
                                    name={field.name}
                                    className={signupVbCheckbox}
                                    showDefaultCheckmarkIcon={false}
                                    checked={!!field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    label={
                                       <span className={signupCheckboxLabel}>
                                          {t('Accept SMS')}
                                       </span>
                                    }
                                 />
                              )}
                           </Field>
                        </div>

                        <div className={termsRow}>
                           <Field name="acceptTerms">
                              {({ field }) => (
                                 <VbCheckbox
                                    id="simple-signup-accept-terms"
                                    name={field.name}
                                    className={termsVbCheckbox}
                                    showDefaultCheckmarkIcon={false}
                                    checked={!!field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    label={
                                       <span className={signupCheckboxLabel}>
                                          {t('I accept the terms')}
                                       </span>
                                    }
                                 />
                              )}
                           </Field>
                        </div>
                        <ErrorMessage name="acceptTerms">
                           {msg => <p className={termsError}>{msg}</p>}
                        </ErrorMessage>

                        <p className={legalNote}>
                           {t(
                              'By registering you agree that we may process your personal data in accordance with our {privacyPolicy}.',
                              { privacyPolicy: '{LINK}' }
                           )
                              .split('{LINK}')
                              .map((part, i) =>
                                 i === 0 ? (
                                    <React.Fragment key={i}>
                                       {part}
                                    </React.Fragment>
                                 ) : (
                                    <React.Fragment key={i}>
                                       <Link to="/integritetspolicy">
                                          {t('privacy policy')}
                                       </Link>
                                       {part}
                                    </React.Fragment>
                                 )
                              )}
                        </p>

                        {status?.apiError ? (
                           <div className="mb-16">
                              <GlobalError>{status.apiError}</GlobalError>
                           </div>
                        ) : null}

                        <VbButton
                           type="submit"
                           className={signUpSubmitButton}
                           styleType="solid"
                           color="blue"
                           uppercase
                           disabled={isSubmitting}
                           data-testid="sign-up-button"
                        >
                           {isSubmitting
                              ? t('Hold on a moment…')
                              : t('Create Profile')}
                        </VbButton>
                     </Form>
                  )}
               </Formik>
            </div>
         </div>
      </div>
   )
}
